import { z } from 'zod';
import { pluginDetailSchema, pluginListItemSchema, pluginSchema } from '../schemas/plugin.js';
import { idParamSchema, listQueryBase, multiFilter, paginatedResponse, reasonSchema, uuidMultiFilter, } from './_shared.js';
const PLUGIN_STATUS = ['active', 'deprecated', 'yanked'];
/**
 * `GET /api/plugins` query — pagination + sort + free-form `search` + status
 * multi-filter + teamId/categoryId.
 *
 * **Migration note:** `status` was previously `z.enum([..., 'all']).default('all')`.
 * It is now a comma-joined multi-value (`multiFilter`). Absent → all statuses
 * (admin default). A lone value (e.g. `'active'`) still parses, preserving
 * existing admin tab + public manifest callers. Passing `status=all` now
 * fails validation — callers must drop the param (absent = all).
 */
export const listPluginsQuerySchema = listQueryBase.extend({
    status: multiFilter(PLUGIN_STATUS),
    /**
     * Hub-listing filter. Omitted → no filter (admin default: every row).
     * `'true'` → only rows NOT hub-pending (the consumer catalog passes this);
     * `'false'` → only hub-pending rows (admin debug surface). String enum, NOT
     * `z.coerce.boolean()` — coercion parses the literal `'false'` as `true`.
     */
    listed: z.enum(['true', 'false']).optional(),
    /**
     * Scope to one or more teams' plugins (server-side `WHERE team_id IN (…)`).
     * Comma-joined UUIDs; a lone UUID still parses, so the Team page caller is
     * unchanged. The Team page passes this so it no longer client-filters a
     * capped global page (which dropped a team's plugins once the catalog
     * crossed `pageSize`); the Dashboard passes the caller's whole team set so
     * its "team plugins" KPI reads a server `COUNT(*)` instead of counting a
     * page. See ADR 0009.
     */
    teamId: uuidMultiFilter(),
    categoryId: z.string().uuid().optional(),
    /**
     * Scope to a single author (server-side `WHERE author_id = …`). Backs the
     * catalog's "Mine" filter and the Dashboard's authored-plugin KPI, both of
     * which previously derived their answer by filtering a capped page.
     */
    authorId: z.string().uuid().optional(),
});
/**
 * Export query: same filters + sort as the list query but without pagination.
 * Used by `GET /api/plugins/export` to stream all matching rows as CSV.
 */
export const exportPluginsQuerySchema = listPluginsQuerySchema.omit({ page: true, pageSize: true });
export const listPluginsResponseSchema = paginatedResponse(pluginListItemSchema);
/** Re-export of the canonical id-param schema for naming clarity at call sites. */
export const pluginIdParamSchema = idParamSchema;
/**
 * `GET /api/plugins/:id` — single-resource fetch. Returns the detail payload
 * (`pluginDetailSchema` = the list row + `readmeContent`, `skills`, and
 * `sourceUrl`) so the plugin detail page renders the README and skill list
 * without a second fetch. Callers that only need the list row can ignore the
 * extra fields.
 */
export const getPluginResponseSchema = pluginDetailSchema;
/**
 * `GET /api/plugins/by-name/:name` path params. `name` reuses
 * `pluginSchema.shape.name` so the kebab-case regex stays in lockstep with the
 * entity row — a malformed slug 400s at the boundary instead of reaching SQL.
 *
 * Exists because `/catalog/:slug` addresses a plugin by name: without a
 * slug-addressed route the UI resolved the slug by scanning a paginated list
 * page, which 404'd every plugin outside the first page. See ADR 0009.
 */
export const getPluginByNameParamsSchema = z.object({ name: pluginSchema.shape.name });
/** Same detail payload as `GET /api/plugins/:id` — one projection, two addresses. */
export const getPluginByNameResponseSchema = pluginDetailSchema;
/**
 * `PATCH /api/plugins/:id` (NEW HANDLER) — admin description edit. Gate
 * `plugins:update`. Audit `plugin.updated`; manifest cache-bust on success.
 * Body is intentionally minimal (only description today) — additional
 * editable fields can be added without breaking older clients.
 */
export const updatePluginBodySchema = z
    .object({
    description: z.string().max(4000).nullable().optional(),
})
    .strict()
    .refine((b) => b.description !== undefined, {
    message: 'at least one editable field must be provided',
});
export const updatePluginResponseSchema = pluginSchema;
/**
 * Lifecycle response (deprecate/yank/unyank/unmark-deprecated). The route
 * returns a thin `{pluginId, status}` ack so the admin UI can update the
 * row without re-fetching the full list. `status` strings here are the
 * lifecycle action names, NOT the canonical `pluginStatusSchema` values
 * (e.g. `unmarked`, `unyanked`) — preserving the legacy admin-hooks
 * shape so UI call sites don't churn.
 */
export const pluginLifecycleResponseSchema = z.object({
    pluginId: z.string().uuid(),
    status: z.enum(['deprecated', 'yanked', 'unyanked', 'unmarked']),
});
export const deprecatePluginBodySchema = z.object({}).strict();
export const deprecatePluginResponseSchema = pluginLifecycleResponseSchema;
/** `reason` ≥ 10 chars enforced — single-word placeholders are rejected. */
export const yankPluginBodySchema = z
    .object({
    reason: reasonSchema,
})
    .strict();
export const yankPluginResponseSchema = pluginLifecycleResponseSchema;
export const unyankPluginBodySchema = z.object({}).strict();
export const unyankPluginResponseSchema = pluginLifecycleResponseSchema;
export const unmarkDeprecatedPluginBodySchema = z.object({}).strict();
export const unmarkDeprecatedPluginResponseSchema = pluginLifecycleResponseSchema;
//# sourceMappingURL=plugins.js.map